import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTenant } from '../../context/TenantContext';

// ASP.NET Core binds repeated query names; axios's default `name[]=` form is ignored by the model binder.
export const serialiseQueryParams = (
    params: Record<string, unknown>
): string => {
    const queryParameters = new URLSearchParams();

    Object.entries(params).forEach(([name, value]) => {
        if (value === undefined || value === null) {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== undefined && item !== null) {
                    queryParameters.append(name, String(item));
                }
            });
            return;
        }

        queryParameters.append(name, String(value));
    });

    return queryParameters.toString();
};

export const AXIOS_INSTANCE = Axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    paramsSerializer: { serialize: serialiseQueryParams },
});

export const useCustomAxios = <T>() => {
    const auth = useAuth();
    const { selectedTenant } = useTenant();
    const accessToken = auth.user?.access_token;
    const tenantId = selectedTenant?.tenantId;

    return useCallback(
        async (config: AxiosRequestConfig): Promise<T> => {
            const headers: Record<string, unknown> = {
                ...(config.headers as Record<string, unknown>),
            };
            if (config.data instanceof FormData) {
                delete headers['Content-Type'];
                delete headers['content-type'];
            }

            const { data } = await AXIOS_INSTANCE({
                ...config,
                headers: {
                    ...(headers as AxiosRequestConfig['headers']),
                    Authorization: `Bearer ${accessToken}`,
                    ...(config.data instanceof FormData
                        ? {}
                        : { 'Content-Type': 'application/json' }),
                    ...(tenantId && {
                        'X-Active-Tenant': tenantId,
                    }),
                },
            });
            return data;
        },
        [accessToken, tenantId]
    );
};

export type ErrorType<Error> = AxiosError<Error>;
